/**
 * Animated Timeline Component
 * Timeline animations for company history, milestones, and achievements
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { animated, useSpring } from '@react-spring/web';
import { 
  useScrollAnimation, 
  useAnimationConfig, 
  useReducedMotion, 
  usePerformanceMonitor 
} from '../lib/animations';
import { 
  Calendar, MapPin, Users, Building, Award, Star, 
  ChevronDown, ChevronUp, ExternalLink, Image as ImageIcon,
  Play, Pause, RotateCcw
} from 'lucide-react';

// Timeline item interface
export interface TimelineItem {
  id: string;
  title: string;
  description: string;
  date: string;
  year: number;
  category?: string;
  location?: string;
  participants?: string[];
  images?: string[];
  icon?: React.ComponentType<any>;
  color?: string;
  importance?: 'low' | 'medium' | 'high' | 'critical';
  details?: string[];
  stats?: { label: string; value: string }[];
  link?: string;
  tags?: string[];
}

// Timeline configuration
export interface TimelineConfig {
  orientation: 'vertical' | 'horizontal';
  layout: 'left' | 'right' | 'alternating' | 'center';
  animationStyle: 'sequential' | 'on-scroll' | 'auto-play' | 'manual';
  showProgress: boolean;
  showYearMarkers: boolean;
  showConnections: boolean;
  interactive: boolean;
  autoPlaySpeed?: number; // seconds
  staggerDelay: number;
  itemSpacing: number;
}

interface AnimatedTimelineProps {
  items: TimelineItem[];
  config: TimelineConfig;
  title?: string;
  subtitle?: string;
  className?: string;
  onItemClick?: (item: TimelineItem) => void;
  triggerOnScroll?: boolean;
  threshold?: number;
}

// Default configuration
const defaultConfig: TimelineConfig = {
  orientation: 'vertical',
  layout: 'alternating',
  animationStyle: 'on-scroll',
  showProgress: true,
  showYearMarkers: true,
  showConnections: true,
  interactive: true,
  staggerDelay: 0.3,
  itemSpacing: 80
};

// Importance colors
const importanceColors = {
  low: '#6b7280',
  medium: '#3b82f6',
  high: '#f59e0b',
  critical: '#ef4444'
};

// Individual Timeline Item Component
interface TimelineItemComponentProps {
  item: TimelineItem;
  index: number;
  isVisible: boolean;
  config: TimelineConfig;
  animationDelay: number;
  isLeft?: boolean;
  onItemClick?: (item: TimelineItem) => void;
}

const TimelineItemComponent: React.FC<TimelineItemComponentProps> = ({
  item,
  index,
  isVisible,
  config,
  animationDelay,
  isLeft = false,
  onItemClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(itemRef, { threshold: 0.3 });
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  const IconComponent = item.icon || Calendar;
  const itemColor = item.color || importanceColors[item.importance || 'medium'];

  // Spring animations for interactions
  const itemSpring = useSpring({
    scale: isHovered ? 1.02 : 1,
    y: isHovered ? -4 : 0,
    config: {
      tension: 300,
      friction: 30
    },
    immediate: isReducedMotion
  });

  // Progress line animation
  const progressSpring = useSpring({
    height: isInView ? '100%' : '0%',
    config: {
      tension: 120,
      friction: 40
    },
    delay: animationDelay * 1000,
    immediate: isReducedMotion
  });

  // Entrance animation variants
  const itemVariants = {
    hidden: {
      opacity: 0,
      x: isLeft ? -50 : 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: animationDelay,
        ease: 'easeOut'
      }
    }
  };

  // Icon animation variants
  const iconVariants = {
    hidden: {
      scale: 0,
      rotate: -180
    },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        delay: animationDelay + 0.2
      }
    }
  };

  const handleClick = () => {
    if (config.interactive) {
      setIsExpanded(!isExpanded);
      if (onItemClick) onItemClick(item);
    }
  };

  return (
    <div 
      ref={itemRef}
      className={`relative flex items-center ${config.orientation === 'vertical' ? 'mb-12' : 'mr-12'}`}
      style={{ 
        marginBottom: config.orientation === 'vertical' ? config.itemSpacing : 0,
        marginRight: config.orientation === 'horizontal' ? config.itemSpacing : 0
      }}
    >
      {/* Timeline Line (Vertical) */}
      {config.orientation === 'vertical' && config.showConnections && (
        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 -top-6 -bottom-6 -z-10">
          <animated.div
            style={progressSpring}
            className="w-full bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"
          />
        </div>
      )}

      {/* Content Container */}
      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        className={`w-full flex items-center ${
          config.layout === 'alternating' 
            ? isLeft ? 'flex-row-reverse' : 'flex-row'
            : config.layout === 'left' ? 'flex-row-reverse' : 'flex-row'
        }`}
      >
        {/* Timeline Icon */}
        <motion.div
          variants={iconVariants}
          className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-4 border-white ${
            config.orientation === 'vertical' ? 'mx-6' : 'my-6'
          }`}
          style={{ backgroundColor: itemColor }}
        >
          <IconComponent size={20} className="text-white" />
          
          {/* Pulse Effect */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: itemColor }}
            animate={!isReducedMotion ? {
              scale: [1, 1.5, 1],
              opacity: [0.7, 0, 0.7]
            } : {}}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: animationDelay
            }}
          />
        </motion.div>

        {/* Content Card */}
        <animated.div
          style={itemSpring}
          className={`flex-1 bg-white rounded-xl shadow-lg border border-gray-100 p-6 cursor-pointer transition-all duration-300 ${
            isHovered ? 'shadow-xl' : ''
          } ${config.layout === 'alternating' ? (isLeft ? 'mr-6' : 'ml-6') : ''}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleClick}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {item.title}
              </h3>
              <div className="flex items-center text-sm text-gray-600 space-x-4">
                <span className="flex items-center">
                  <Calendar size={14} className="mr-1" />
                  {item.date}
                </span>
                {item.location && (
                  <span className="flex items-center">
                    <MapPin size={14} className="mr-1" />
                    {item.location}
                  </span>
                )}
              </div>
            </div>
            
            {/* Year Badge */}
            <motion.div
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: animationDelay + 0.3, type: 'spring' }}
            >
              {item.year}
            </motion.div>
          </div>

          {/* Description */}
          <p className="text-gray-700 mb-4">
            {item.description}
          </p>

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {item.tags.map((tag, tagIndex) => (
                <motion.span
                  key={tag}
                  className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: animationDelay + 0.4 + (tagIndex * 0.1) }}
                >
                  {tag}
                </motion.span>
              ))}
            </div>
          )}

          {/* Expandable Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-gray-100 pt-4 mt-4"
              >
                {/* Details */}
                {item.details && item.details.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Details</h4>
                    <ul className="space-y-1">
                      {item.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="text-sm text-gray-600 flex items-start">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-2 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Stats */}
                {item.stats && item.stats.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Statistics</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {item.stats.map((stat, statIndex) => (
                        <div key={statIndex} className="text-center p-2 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                          <div className="text-xs text-gray-600">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Participants */}
                {item.participants && item.participants.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Participants</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.participants.map((participant, participantIndex) => (
                        <span
                          key={participantIndex}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {participant}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Link */}
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={14} className="mr-1" />
                    Learn More
                  </a>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Expand/Collapse Indicator */}
          {config.interactive && (item.details || item.stats || item.participants) && (
            <motion.div
              className="flex items-center justify-center mt-4 text-gray-400 hover:text-gray-600"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={20} />
            </motion.div>
          )}
        </animated.div>
      </motion.div>
    </div>
  );
};

// Main Timeline Component
export const AnimatedTimeline: React.FC<AnimatedTimelineProps> = ({
  items,
  config: userConfig,
  title,
  subtitle,
  className = '',
  onItemClick,
  triggerOnScroll = true,
  threshold = 0.1
}) => {
  const config = { ...defaultConfig, ...userConfig };
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const scrollAnimation = useScrollAnimation(threshold, true);
  const isVisible = triggerOnScroll ? scrollAnimation.isVisible : true;
  const animationConfig = useAnimationConfig();
  const { trackAnimation } = usePerformanceMonitor();

  // Auto-play functionality
  useEffect(() => {
    if (config.animationStyle === 'auto-play' && isPlaying && config.autoPlaySpeed) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % items.length);
      }, config.autoPlaySpeed * 1000);

      return () => clearInterval(interval);
    }
  }, [isPlaying, config.animationStyle, config.autoPlaySpeed, items.length]);

  // Sort items by year
  const sortedItems = [...items].sort((a, b) => a.year - b.year);

  // Calculate animation delays
  const getAnimationDelay = (index: number): number => {
    switch (config.animationStyle) {
      case 'sequential':
        return index * config.staggerDelay;
      case 'auto-play':
        return index <= currentIndex ? 0 : 999; // Only show up to current index
      case 'manual':
        return index <= currentIndex ? 0 : 999;
      default: // on-scroll
        return index * config.staggerDelay;
    }
  };

  // Progress calculation
  const progressPercentage = config.animationStyle === 'auto-play' || config.animationStyle === 'manual'
    ? ((currentIndex + 1) / items.length) * 100
    : 100;

  return (
    <div 
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`w-full ${className}`}
    >
      {/* Header */}
      {(title || subtitle) && (
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -30 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {title && (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>
      )}

      {/* Controls */}
      {(config.animationStyle === 'auto-play' || config.animationStyle === 'manual') && (
        <motion.div
          className="flex items-center justify-center space-x-4 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ delay: 0.3 }}
        >
          {config.animationStyle === 'auto-play' && (
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              {isPlaying ? <Pause size={16} className="mr-2" /> : <Play size={16} className="mr-2" />}
              {isPlaying ? 'Pause' : 'Play'}
            </button>
          )}
          
          <button
            onClick={() => setCurrentIndex(0)}
            className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <RotateCcw size={16} className="mr-2" />
            Reset
          </button>

          <span className="text-sm text-gray-600">
            {currentIndex + 1} of {items.length}
          </span>
        </motion.div>
      )}

      {/* Progress Bar */}
      {config.showProgress && (
        <motion.div
          className="w-full max-w-4xl mx-auto mb-8"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: isVisible ? 1 : 0, scaleX: isVisible ? 1 : 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </motion.div>
      )}

      {/* Timeline Items */}
      <div className={`relative ${
        config.orientation === 'vertical' 
          ? 'max-w-4xl mx-auto' 
          : 'flex overflow-x-auto pb-4'
      }`}>
        {sortedItems.map((item, index) => (
          <TimelineItemComponent
            key={item.id}
            item={item}
            index={index}
            isVisible={isVisible}
            config={config}
            animationDelay={getAnimationDelay(index)}
            isLeft={config.layout === 'alternating' ? index % 2 === 0 : false}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </div>
  );
};

// Preset timeline configurations
export const timelinePresets = {
  companyHistory: {
    orientation: 'vertical' as const,
    layout: 'alternating' as const,
    animationStyle: 'on-scroll' as const,
    showProgress: true,
    showYearMarkers: true,
    showConnections: true,
    interactive: true,
    staggerDelay: 0.2,
    itemSpacing: 100
  },
  
  projectMilestones: {
    orientation: 'horizontal' as const,
    layout: 'center' as const,
    animationStyle: 'sequential' as const,
    showProgress: true,
    showYearMarkers: false,
    showConnections: true,
    interactive: true,
    staggerDelay: 0.3,
    itemSpacing: 120
  },
  
  achievementShowcase: {
    orientation: 'vertical' as const,
    layout: 'left' as const,
    animationStyle: 'auto-play' as const,
    showProgress: true,
    showYearMarkers: true,
    showConnections: false,
    interactive: false,
    autoPlaySpeed: 3,
    staggerDelay: 0.1,
    itemSpacing: 80
  }
};

export default AnimatedTimeline;